import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  Row,
  Col,
  Button,
  Tag,
  Space,
  Switch,
  message,
  Modal,
  Form,
  Input,
  Spin,
  Alert,
  Tooltip,
  Drawer,
  Timeline,
  Empty,
  Divider,
} from 'antd';
import { 
  AppstoreOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  ApiOutlined,
  LinkOutlined,
  GoogleOutlined,
  GithubOutlined,
  WindowsOutlined,
  SlackOutlined,
  SyncOutlined,
  DisconnectOutlined,
  CloseCircleOutlined,
  FileSearchOutlined,
  FormOutlined,
  QuestionCircleOutlined,
  EditOutlined,
} from '@ant-design/icons';
import type { ClientProduct } from '../../../shared/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase, functions } from '../../../shared/lib/supabase';
import { useToggleClientProduct, useUncontractProduct } from '../hooks/useClientProducts';
import { 
  useWorkflowCredentials, 
  useCreateOAuthCredential, 
  useDeleteOAuthCredential,
  useTelegramWebhookInfo,
  useCreateTelegramCredential,
} from '../hooks/useOAuthCredentials';
import { useAuthStore } from '@/features/auth/store/authStore';
import { FormRenderer } from '../components/FormRenderer';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

// Telegram Credentials Section Component
interface TelegramCredentialsSectionProps {
  clientProduct: ClientProduct;
}

const TelegramCredentialsSection: React.FC<TelegramCredentialsSectionProps> = ({ clientProduct }) => {
  const [accessToken, setAccessToken] = useState('');
  const [isConfiguring, setIsConfiguring] = useState(false);
  const { data: telegramInfo, isLoading, error } = useTelegramWebhookInfo(clientProduct.id);
  const createTelegramCredential = useCreateTelegramCredential();

  const handleSaveToken = async () => {
    if (!accessToken.trim()) {
      message.warning('Por favor, insira o Access Token do Telegram');
      return;
    }

    try {
      setIsConfiguring(true);
      
      const actionText = telegramInfo?.hasCredential ? 'Atualizando' : 'Configurando';
      message.loading({ content: `${actionText} credencial...`, key: 'telegram' });

      await createTelegramCredential.mutateAsync({
        client_product_id: clientProduct.id,
        access_token: accessToken.trim(),
      });

      const successText = telegramInfo?.hasCredential 
        ? 'Credencial do Telegram atualizada com sucesso! A credencial anterior foi removida.'
        : 'Credencial do Telegram configurada com sucesso!';

      message.success({ 
        content: successText,
        key: 'telegram',
        duration: 5,
      });
      setAccessToken('');
      setIsConfiguring(false);
    } catch (error: any) {
      message.error({ 
        content: error.message || 'Erro ao configurar credencial',
        key: 'telegram',
      });
      setIsConfiguring(false);
    }
  };

  const handleCopyWebhook = () => {
    if (telegramInfo?.webhookUrl) {
      navigator.clipboard.writeText(telegramInfo.webhookUrl);
      message.success('URL do Webhook copiada!');
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        background: '#fafafa', 
        padding: 12, 
        borderRadius: 6,
        border: '1px solid #e8e8e8',
        textAlign: 'center'
      }}>
        <Spin size="small" />
        <span style={{ marginLeft: 8, fontSize: 12, color: '#666' }}>
          Verificando configuração do Telegram...
        </span>
      </div>
    );
  }

  if (error || !telegramInfo) {
    // Não tem nó do Telegram, não mostra nada
    return null;
  }

  return (
    <div style={{ 
      background: '#fafafa', 
      padding: 14, 
      borderRadius: 8,
      border: '1px solid #e8e8e8',
    }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Space>
            <ApiOutlined style={{ fontSize: 18, color: '#0088cc' }} />
            <Text strong style={{ fontSize: 13 }}>Telegram Bot</Text>
          </Space>
          {telegramInfo.hasCredential && (
            <Tag color="success" icon={<CheckCircleOutlined />}>
              Conectado
            </Tag>
          )}
        </div>

        {/* Webhook URL */}
        <div>
          <div style={{ marginBottom: 6 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              URL do Webhook
            </Text>
            <Tooltip title="Cole esta URL nas configurações do seu bot no Telegram">
              <QuestionCircleOutlined style={{ marginLeft: 4, fontSize: 12, color: '#8c8c8c' }} />
            </Tooltip>
          </div>
          <Input.Group compact>
            <Input
              value={telegramInfo.webhookUrl}
              readOnly
              style={{ width: 'calc(100% - 80px)' }}
              size="small"
            />
            <Button 
              icon={<LinkOutlined />} 
              size="small"
              onClick={handleCopyWebhook}
            >
              Copiar
            </Button>
          </Input.Group>
        </div>

        {/* Access Token - sempre mostrar para permitir alterar */}
        <div>
          <div style={{ marginBottom: 6 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Access Token do Bot
            </Text>
            <Tooltip title="Obtenha o token com o @BotFather no Telegram">
              <QuestionCircleOutlined style={{ marginLeft: 4, fontSize: 12, color: '#8c8c8c' }} />
            </Tooltip>
          </div>
          <Input.Group compact>
            <Input.Password
              placeholder={telegramInfo.hasCredential ? "Digite novo token (opcional)" : "Digite o access token..."}
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              onPressEnter={handleSaveToken}
              style={{ width: 'calc(100% - 80px)' }}
              size="small"
            />
            <Button 
              type="primary"
              size="small"
              onClick={handleSaveToken}
              loading={isConfiguring}
              disabled={!accessToken.trim()}
            >
              {telegramInfo.hasCredential ? 'Atualizar' : 'Salvar'}
            </Button>
          </Input.Group>
          {!telegramInfo.hasCredential && (
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
              💡 Configure o webhook no Telegram usando a URL acima antes de salvar o token
            </Text>
          )}
        </div>

        {telegramInfo.hasCredential && (
          <Alert
            type="success"
            message="Bot do Telegram conectado"
            description={
              <div>
                <div style={{ marginBottom: 4 }}>
                  <strong>Credencial:</strong> {telegramInfo.credentialName || 'Telegram Bot'}
                </div>
                <div style={{ marginBottom: 4, fontFamily: 'monospace', fontSize: 11 }}>
                  <strong>Token:</strong> <span style={{ color: '#8c8c8c' }}>••••••••••••••••••••••••••••••••</span>
                </div>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                  💡 Use o campo acima para reenviar um novo token se precisar trocar o bot.
                </div>
              </div>
            }
            showIcon
            style={{ fontSize: 12 }}
          />
        )}
      </Space>
    </div>
  );
};

// OAuth Credentials Section Component
interface OAuthCredentialsSectionProps {
  clientProduct: ClientProduct;
  onConnect: (credential: any) => void;
}

const OAuthCredentialsSection: React.FC<OAuthCredentialsSectionProps> = ({ clientProduct, onConnect }) => {
  const { data: credentialsData, isLoading, error } = useWorkflowCredentials(clientProduct.id);
  const deleteCredential = useDeleteOAuthCredential();
  
  const handleDisconnect = async (credentialType: string) => {
    Modal.confirm({
      title: 'Desconectar API',
      content: 'Tem certeza que deseja desconectar esta API? Você precisará reconectar para usar este produto.',
      okText: 'Sim, desconectar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          message.loading({ content: 'Desconectando...', key: 'disconnect' });
          
          await deleteCredential.mutateAsync({
            client_product_id: clientProduct.id,
            credential_type: credentialType,
          });
          
          message.success({ 
            content: 'API desconectada com sucesso!',
            key: 'disconnect',
          });
          
        } catch (error: any) {
          message.error({ 
            content: error.message || 'Erro ao desconectar API',
            key: 'disconnect',
          });
        }
      },
    });
  };

  if (isLoading) {
    return (
      <div style={{ 
        background: '#fafafa', 
        padding: 12, 
        borderRadius: 6,
        border: '1px solid #e8e8e8',
        textAlign: 'center'
      }}>
        <Spin size="small" />
        <span style={{ marginLeft: 8, fontSize: 12, color: '#666' }}>
          Verificando conexões...
        </span>
      </div>
    );
  }
      
  if (error) {
    return (
      <div style={{ 
        background: '#fff2e8', 
        padding: 12, 
        borderRadius: 6,
        border: '1px solid #ffbb96',
      }}>
        <Space>
          <ApiOutlined style={{ color: '#fa8c16' }} />
          <span style={{ fontSize: 12, color: '#ad6800' }}>
            Erro ao verificar conexões API
          </span>
        </Space>
      </div>
    );
  }

  // Se não tem workflow clonado (ou custom) ainda, não mostra nada
  if (!clientProduct.cloned_workflow_id && !clientProduct.custom_workflow_id) {
    return null;
  }

  if (!credentialsData || credentialsData.credentials.length === 0) {
    return null;
  }

  const getCredentialIcon = (type: string) => {
    const icons: Record<string, any> = {
      googleCalendarOAuth2Api: <GoogleOutlined style={{ color: '#4285F4' }} />,
      googleDriveOAuth2Api: <GoogleOutlined style={{ color: '#4285F4' }} />,
      googleSheetsOAuth2Api: <GoogleOutlined style={{ color: '#4285F4' }} />,
      gmailOAuth2: <GoogleOutlined style={{ color: '#4285F4' }} />,
      githubOAuth2Api: <GithubOutlined />,
      microsoftOAuth2Api: <WindowsOutlined style={{ color: '#00A4EF' }} />,
      slackOAuth2Api: <SlackOutlined style={{ color: '#4A154B' }} />,
    };
    return icons[type] || <ApiOutlined />;
  };

  return (
    <div style={{ 
      background: '#fafafa', 
      padding: 12, 
      borderRadius: 6,
      border: '1px solid #e8e8e8',
    }}>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: 8,
        }}>
          <Space>
            <ApiOutlined style={{ fontSize: 16, color: '#1890ff' }} />
            <span style={{ fontWeight: 500, fontSize: 14 }}>
              Conexões API
            </span>
          </Space>
        </div>

        {credentialsData.credentials.length === 0 && (
          <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>
            Este produto não requer conexões API.
          </Paragraph>
        )}

        {credentialsData.credentials.map((credential) => (
          <div 
            key={credential.id}
            style={{ 
              background: 'white',
              padding: 12,
              borderRadius: 6,
              border: `1px solid ${credential.isConfigured ? '#52c41a' : '#d9d9d9'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            {/* Ícone e Info */}
            <Space size={12} style={{ flex: 1 }}>
              <div style={{ fontSize: 24 }}>
                {getCredentialIcon(credential.type)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  fontWeight: 500, 
                  fontSize: 13,
                  marginBottom: 2,
                }}>
                  {credential.displayName}
                </div>
                <Space size={8}>
                  <Tag 
                    color={credential.isConfigured ? 'success' : 'default'}
                    style={{ margin: 0, fontSize: 11 }}
                  >
                    {credential.isConfigured ? (
                      <>
                        <CheckCircleOutlined /> Conectado
                      </>
                    ) : (
                      <>
                        <CloseCircleOutlined /> Desconectado
                      </>
                    )}
                  </Tag>
                </Space>
              </div>
            </Space>

            {/* Ações */}
            <Space size={4}>
              {credential.isConfigured ? (
                <>
                  <Tooltip title="Reconectar">
                    <Button
                      type="text"
                      size="small"
                      icon={<SyncOutlined />}
                      onClick={() => onConnect(credential)}
                    />
                  </Tooltip>
                  <Tooltip title="Desconectar">
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DisconnectOutlined />}
                      onClick={() => handleDisconnect(credential.type)}
                    />
                  </Tooltip>
                </>
              ) : (
                <Button
                  type="primary"
                  size="small"
                  icon={<LinkOutlined />}
                  onClick={() => onConnect(credential)}
                >
                  Conectar
                </Button>
              )}
            </Space>
          </div>
        ))}
      </Space>
    </div>
  );
};

const MyProductsPage: React.FC = () => {
  const [oauthModalOpen, setOauthModalOpen] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState<any>(null);
  const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
  const [logsProduct, setLogsProduct] = useState<ClientProduct | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsProduct, setDetailsProduct] = useState<ClientProduct | null>(null);
  const [togglingProducts, setTogglingProducts] = useState<Set<string>>(new Set());
  
  // Form modal states
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formWasApproved, setFormWasApproved] = useState(false);
  const [selectedClientProductId, setSelectedClientProductId] = useState<string | null>(null);
  const [formData, setFormData] = useState<any>(null);
  const [loadingForm, setLoadingForm] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formInitialValues, setFormInitialValues] = useState<Record<string, any> | null>(null);
  const [formSubmissionId, setFormSubmissionId] = useState<string | null>(null);
  const [oauthForm] = Form.useForm();
   const { user } = useAuthStore();
   const clientId = user?.id ?? null;
  const location = useLocation();
  const navigate = useNavigate();

  // Live credential status for the selected product (used in modal alerts)
  const { data: detailsCredentialsData, isLoading: loadingDetailsCredentials } = useWorkflowCredentials(detailsProduct?.id ?? null);
  const { data: detailsTelegramInfo } = useTelegramWebhookInfo(detailsProduct?.id ?? null);

  // Busca produtos contratados do cliente logado e status do workflow no n8n
  const { data: clientProducts = [], isLoading } = useQuery({
    queryKey: ['client-products', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      if (!clientId) return [];
      
      // Buscar produtos com informações de pagamento
      const { data, error } = await supabase
        .from('client_products_with_subscription')
        .select(`
          *, 
          product:products(*, n8n_instance:n8n_instances(*)),
          subscription:client_subscriptions(
            id,
            status,
            payment_status,
            next_due_date,
            billing_type,
            trial_end
          )
        `)
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('❌ Error loading client products:', error);
        throw error;
      }
      
      // Deduplicate by ID
      const uniqueProducts = data ? Array.from(
        new Map(data.map(item => [item.id, item])).values()
      ) : [];

      // Buscar status do workflow no n8n para cada produto
      const productsWithStatus = await Promise.all(uniqueProducts.map(async (item) => {
        let workflowStatus = 'unknown';
        try {
          // Priorizar custom_workflow_id para produtos customizados, cloned_workflow_id para templates clonados
          const workflowId = item.custom_workflow_id || item.cloned_workflow_id || item.product?.workflow_id;
          
          if (workflowId && item.product?.n8n_instance_id) {
            // Primeiro, buscar a instância n8n com a api_key descriptografada
            let instanceData: any = null;
            try {
              instanceData = await functions.invoke('get-n8n-instance', { body: { n8n_instance_id: item.product.n8n_instance_id } });
            } catch (instanceError: any) {
              console.warn('⚠️ Failed to fetch n8n instance:', instanceError);
            }

            if (!instanceData) {
              workflowStatus = 'unknown';
            } else {
              console.log('✅ N8n instance fetched:', {
                id: instanceData.id,
                has_api_key: !!instanceData.api_key,
              });

              // Agora buscar o status do workflow
              const response = await functions.getWorkflowStatus({
                workflow_id: workflowId,
                n8n_instance: {
                  api_url: instanceData.api_url,
                  api_key: instanceData.api_key,
                },
              });
              workflowStatus = response.workflow_status || 'unknown';
              console.log('✅ Workflow status fetched:', {
                workflow_id: workflowId,
                status: workflowStatus,
              });
            }
          } else {
            console.warn('⚠️ Missing workflow_id or n8n_instance_id for product:', item.id);
          }
        } catch (err) {
          console.error('❌ Erro ao buscar status do workflow no n8n:', err);
          workflowStatus = 'unknown';
        }
        return {
          ...item,
          workflow_status: workflowStatus,
        };
      }));
      return productsWithStatus as ClientProduct[];
    },
  });
  const queryClient = useQueryClient();

  const { data: productLogs = [], isLoading: loadingLogs } = useQuery({
    queryKey: ['client-product-logs', logsProduct?.id],
    enabled: !!logsProduct?.id,
    queryFn: async () => {
      if (!logsProduct?.id) return [];
      const { data, error } = await supabase
        .from('activity_log')
        .select('id,action_type,description,metadata,created_at')
        .eq('client_product_id', logsProduct.id)
        .order('created_at', { ascending: false })
        .limit(30);

      if (error) {
        if (error.message.includes('does not exist')) {
          return [];
        }
        throw error;
      }

      return data ?? [];
    },
  });

  const formatCurrency = (value?: number | null) => {
    if (!value) return 'R$ 0,00';
    return Number(value).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const hasCredentialsRequirement = (source: any) => {
    if (!source) return false;
    if (Array.isArray(source)) return source.length > 0;
    if (typeof source === 'object') return Object.keys(source).length > 0;
    return Boolean(source);
  };

  const computeApiRequirement = (clientProduct: ClientProduct, opts?: { oauthCreds?: any[]; telegramConnected?: boolean }) => {
    const requiresOAuth = Boolean((clientProduct.product as any)?.requires_oauth);
    const oauthRequirement = hasCredentialsRequirement((clientProduct as any).oauth_credentials) ||
      hasCredentialsRequirement((clientProduct.product as any)?.oauth_credentials);
    const hasWorkflow = Boolean(clientProduct.cloned_workflow_id || clientProduct.custom_workflow_id);

    const requiresApi = requiresOAuth || oauthRequirement || hasWorkflow;

    // Prefer live data from hooks when available
    const allOauthConfigured = Array.isArray(opts?.oauthCreds) && opts!.oauthCreds.length > 0
      ? opts!.oauthCreds.every((c) => c.isConfigured)
      : false;
    const telegramConnected = Boolean(opts?.telegramConnected);

    // Fallback: if we have mapped oauth_credentials persisted, consider API connected
    const hasMappedOauth = hasCredentialsRequirement((clientProduct as any).oauth_credentials);

    const hasApiConnected = Boolean(
      allOauthConfigured ||
      telegramConnected ||
      hasMappedOauth ||
      (clientProduct as any).has_oauth_credentials_configured ||
      (clientProduct as any).has_api_credentials_configured ||
      (clientProduct as any).api_connected
    );

    return { requiresApi, hasApiConnected };
  };



  const handleViewLogs = (clientProduct: ClientProduct) => {
    setLogsProduct(clientProduct);
    setLogsDrawerOpen(true);
  };

  const handleViewDetails = async (clientProduct: ClientProduct) => {
    setDetailsProduct(clientProduct);
    setDetailsModalOpen(true);
    // Fetch last submission metadata to show any missing_fields in the details modal
    try {
      const { data: lastSubmission, error } = await supabase
        .from('form_submissions')
        .select('id, metadata, form_data, status, submitted_at, updated_at')
        .eq('client_product_id', clientProduct.id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!error && lastSubmission) {
        setDetailsProduct({ ...clientProduct, last_submission: lastSubmission } as any);
      }
    } catch (err) {
      console.warn('Error fetching last submission for details modal', err);
    }
  };

  const mapActionLabel = (actionType?: string | null) => {
    if (!actionType) return 'Atualização';
    const dictionary: Record<string, string> = {
      workflow_exec: 'Execução de workflow',
      workflow_activated: 'Workflow ativado',
      workflow_deactivated: 'Workflow desativado',
      workflow_failed: 'Falha na execução',
      credential_updated: 'Credencial atualizada',
    };
    return dictionary[actionType] ?? actionType.replace(/_/g, ' ');
  };

  const timelineColor = (actionType?: string | null) => {
    if (!actionType) return 'blue';
    if (actionType.includes('failed')) return 'red';
    if (actionType.includes('deactivated')) return 'gray';
    if (actionType.includes('activated')) return 'green';
    return 'blue';
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const productId = params.get('product');
    const tab = params.get('tab');

    if (productId && tab === 'logs' && clientProducts.length) {
      const product = clientProducts.find((item) => item.id === productId);
      if (product) {
        handleViewLogs(product);
      }
    }
    
    // Abrir modal de detalhes se vier do Dashboard
    const state = (location.state as any);
    if (state?.openDetailsFor && clientProducts.length) {
      const product = clientProducts.find((item) => item.id === state.openDetailsFor);
      if (product) {
        handleViewDetails(product);
        // Limpar o state após abrir o modal
        window.history.replaceState({}, document.title);
      }
    }
  }, [clientProducts, location.search, location.state]);

  const toggleActive = useToggleClientProduct();
  const uncontractProduct = useUncontractProduct();
  
  // OAuth credentials management
  const createOAuthCredential = useCreateOAuthCredential();

  const handleToggleActive = async (id: string, isActive: boolean, clientProduct: ClientProduct) => {
    // Impedir cliques múltiplos
    if (togglingProducts.has(id)) {
      return;
    }

    // ====== DEBUG - LOG TEMPORÁRIO ======
    console.log('🔍 DEBUG Toggle Active:', {
      product_name: clientProduct.product?.name,
      isActive,
      form_id: clientProduct.form_id,
      product_form_id: (clientProduct.product as any)?.form_id,
      requires_form_approval: clientProduct.requires_form_approval,
      form_approval_status: clientProduct.form_approval_status,
      has_form_trigger: (clientProduct.product as any)?.has_form_trigger,
      form_record_id: clientProduct.form_record_id,
    });
    // ====================================

    // VALIDAÇÃO 1: FORMULÁRIO OBRIGATÓRIO (PRIORIDADE MÁXIMA)
    // Considera obrigatório se flagged para aprovação OU se existir qualquer form vinculado
    const hasForm = Boolean(
      clientProduct.form_id ||
      (clientProduct.product as any)?.form_id
    );
    const hasFormTrigger = Boolean((clientProduct.product as any)?.has_form_trigger);
    const mustEnforceForm = clientProduct.requires_form_approval || hasForm || hasFormTrigger;
    const formStatus = clientProduct.form_approval_status;
    const isFormBlocking = mustEnforceForm && formStatus !== 'approved';
    
    console.log('🔍 DEBUG Form Check:', {
      hasForm,
      hasFormTrigger,
      mustEnforceForm,
      form_id: clientProduct.form_id,
      product_form_id: (clientProduct.product as any)?.form_id,
      requires_form_approval: clientProduct.requires_form_approval,
      form_approval_status: clientProduct.form_approval_status,
    });

    // Se está tentando ATIVAR e deve exigir formulário aprovado
    if (isActive && mustEnforceForm) {
      console.log('✅ Produto exige formulário, verificando status...');

      console.log('🔒 Validação de formulário. Status atual:', formStatus);

      // Bloquear se status não é 'approved'
      if (formStatus !== 'approved') {
        console.log('❌ BLOQUEADO: Formulário não aprovado');

        // Mensagens específicas por status
        if (!formStatus || formStatus === 'pending_submission') {
          message.warning({
            content: '📝 Você precisa preencher e enviar o formulário antes de ativar este produto',
            duration: 5,
          });
          return;
        }

        if (formStatus === 'submitted') {
          message.warning({
            content: '⏳ O formulário está em análise. Aguarde a aprovação antes de ativar o produto',
            duration: 5,
          });
          return;
        }

        if (formStatus === 'rejected') {
          message.error({
            content: '❌ O formulário foi reprovado. Corrija as informações antes de ativar o produto',
            duration: 5,
          });
          return;
        }

        // Fallback para qualquer outro status não aprovado
        message.warning({
          content: '📋 Você precisa ter o formulário aprovado antes de ativar este produto',
          duration: 5,
        });
        return;
      }

      console.log('✅ Formulário aprovado, pode continuar');
    }
    
    // Verificar status de pagamento (apenas para produtos PAGO, não para FREE)
    const isFreeProduct = (clientProduct.product?.monthly_price ?? 0) <= 0;
    const subscriptionRaw = (clientProduct as any).subscription;
    const subscription = Array.isArray(subscriptionRaw) ? subscriptionRaw[0] : subscriptionRaw;
    const paymentStatus = subscription?.payment_status || clientProduct.payment_status;
    const trialEnd = subscription?.trial_end;
    const isInTrial = trialEnd && new Date(trialEnd) > new Date();
    const isPaymentBlocking = !isFreeProduct && !isInTrial && paymentStatus && !['paid', 'succeeded'].includes(paymentStatus);

    // OAuth/API blocking (avaliar por produto)
    const { requiresApi, hasApiConnected } = computeApiRequirement(clientProduct);
    const isOAuthBlocking = requiresApi && !hasApiConnected;

    if (isActive && isOAuthBlocking) {
      message.warning({
        content: 'Conecte a API antes de ativar este produto.',
        duration: 5,
      });
      return;
    }

    if (isActive && isFormBlocking) {
      // já coberto acima, mas manter a ordem de bloqueios
      message.warning({
        content: 'Preencha e envie o formulário para ativar este produto.',
        duration: 5,
      });
      return;
    }

    if (isActive && isPaymentBlocking) {
      if (paymentStatus === 'overdue') {
        message.error({
          content: 'Seu pagamento está vencido. Regularize para ativar o produto.',
          duration: 5,
        });
      } else {
        message.warning({
          content: 'Pagamento pendente. Aguarde a confirmação antes de ativar o produto.',
          duration: 5,
        });
      }
      return;
    }
    
    // Adicionar ao conjunto de produtos sendo processados
    setTogglingProducts(prev => new Set(prev).add(id));
    
    try {
      await toggleActive.mutateAsync({ id, isActive });
      message.success(`Produto ${isActive ? 'ativado' : 'desativado'} com sucesso!`);
    } catch (error: any) {
      // Extract detailed error message
      let errorMessage = 'Ocorreu um erro inesperado. Tente novamente ou contate o suporte.';
      let requiresSupport = false;
      
      if (error?.message) {
        errorMessage = error.message;
        
        // Check if error requires support contact
        if (error.message.includes('suporte') || error.message.includes('contato')) {
          requiresSupport = true;
        }
      }
      
      // Show error message with support link if needed
      if (requiresSupport) {
        const friendlyMessage =
          'Problema na ativação do produto\n' +
          'A ativação do produto não está sendo concluída por erro na configuração do workflow. Favor verificar e orientar sobre a correção necessária.';
        const supportParams = new URLSearchParams({
          workflow: clientProduct.id,
          description: friendlyMessage,
        });
        Modal.error({
          title: 'Não foi possível ativar o produto',
          content: (
            <div>
              <p style={{ marginBottom: 0 }}>{friendlyMessage}</p>
            </div>
          ),
          okText: 'Abrir suporte',
          onOk: () => navigate(`/client/support?${supportParams.toString()}`),
        });
      } else {
        message.error({
          content: errorMessage,
          duration: 6,
        });
      }
      
      console.error('Error toggling product:', error);
    } finally {
      // Remover do conjunto de produtos sendo processados
      setTogglingProducts(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleUncontract = async (id: string, productName: string) => {
    Modal.confirm({
      title: 'Descontratar Produto',
      content: `Tem certeza que deseja descontratar "${productName}"? Esta ação não pode ser desfeita e o workflow clonado será removido.`,
      okText: 'Sim, descontratar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await uncontractProduct.mutateAsync(id);
          message.success('Produto descontratado com sucesso!');
        } catch (error) {
          console.error('Erro ao descontratar produto:', error);
          message.error('Erro ao descontratar produto');
        }
      },
    });
  };

  const handleOpenFormModal = async (clientProductId: string, formId: string, approvalStatus?: string) => {
    try {
      setLoadingForm(true);
      setSelectedClientProductId(clientProductId);

      // Load form data
      const { data, error } = await supabase
        .from('forms')
        .select('*')
        .eq('id', formId)
        .eq('is_active', true)
        .single();

      if (error) throw error;

      if (!data) {
        message.error('Formulário não encontrado ou inativo');
        return;
      }

      setFormData(data);
      setFormSubmitted(false);

      // Load any existing submission for this client product
      try {
        const { data: existing, error: existingErr } = await supabase
          .from('form_submissions')
          .select('*')
          .eq('client_product_id', clientProductId)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (existingErr) {
          console.warn('Error loading existing submission:', existingErr);
        }
        if (existing) {
          // If an existing submission already exists, determine if it has been finally submitted
          setFormInitialValues(existing.form_data ?? existing.data ?? null);
          setFormSubmissionId(existing.id);
          setFormSubmitted(existing.status === 'submitted');
        } else {
          setFormInitialValues(null);
          setFormSubmissionId(null);
          setFormSubmitted(false);
        }
      } catch (e) {
        console.warn('Error fetching existing submission', e);
      }
      // For previously approved forms, show a notice in the modal and alter submit behavior
      setFormModalOpen(true);
      if (approvalStatus === 'approved') {
        setFormWasApproved(true);
      } else {
        setFormWasApproved(false);
      }
    } catch (error: any) {
      console.error('Error loading form:', error);
      message.error('Erro ao carregar formulário');
    } finally {
      setLoadingForm(false);
    }
  };

  const handleFormSubmitSuccess = () => {
    setFormSubmitted(true);
    message.success('Formulário enviado com sucesso!');
    // Reload products to update status
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  const handleOpenOAuthModal = (credential: any) => {
    console.log('🔓 Opening OAuth modal for credential:', credential);
    setSelectedCredential(credential);
    setOauthModalOpen(true);
    oauthForm.resetFields();
  };

  const handleCreateOAuthCredential = async () => {
    console.log('🎯 handleCreateOAuthCredential called', { detailsProduct, selectedCredential });
    
    if (!detailsProduct || !selectedCredential) {
      console.warn('⚠️ Missing detailsProduct or selectedCredential');
      return;
    }

    try {
      console.log('📝 Validating form fields...');
      const values = await oauthForm.validateFields();
      console.log('✅ Form validated:', values);
      
      message.loading({ content: 'Criando credencial...', key: 'createOAuth' });
      
      const result = await createOAuthCredential.mutateAsync({
        client_product_id: detailsProduct.id,
        credential_type: selectedCredential.type,
        credential_data: values,
      });
      
      console.log('📦 Create OAuth Result:', result);
      
      message.success({ 
        content: result.message, 
        key: 'createOAuth',
        duration: 5,
      });

      // Guardar dados antes de limpar estado para uso no fluxo do OAuth
      const currentCredential = selectedCredential;
      const currentProduct = detailsProduct;
      const currentValues = values;

      console.log('🚀 OAuth Flow Debug:', {
        credential_id: result.credential_id,
        currentCredential,
        currentProduct,
        currentValues,
        hasCredentialId: !!result.credential_id,
        hasCurrentCredential: !!currentCredential,
        shouldShowModal: !!(result.credential_id && currentCredential)
      });

      setOauthModalOpen(false);
      setSelectedCredential(null);
      oauthForm.resetFields();

      // Agora vamos fazer o OAuth flow direto no frontend
      if (result.credential_id && currentCredential) {
        console.log('✅ Opening OAuth modal...');
        // Criar um listener para receber o código do OAuth
        const handleOAuthMessage = async (event: MessageEvent) => {
          if (event.data.type === 'oauth_code') {
            const { code } = event.data;
            
            try {
              message.loading({ content: 'Finalizando autenticação...', key: 'finalizeOAuth' });
              
              const requestBody = {
                code,
                credential_id: result.credential_id,
                client_product_id: currentProduct.id,
                client_id: currentValues.clientId,
                client_secret: currentValues.clientSecret,
                redirect_uri: redirectUri, // Enviar o mesmo redirect_uri usado na autorização
                credential_type: currentCredential.type,
                credential_name: result.credential_name,
              };
              
              console.log('🔧 Finalize OAuth Request:', requestBody);
              
              // Chamar função para trocar code por tokens e atualizar credencial
              const data = await functions.finalizeOAuth(requestBody);
              
              console.log('📡 Finalize OAuth Response:', data);

              if (data.error) {
                console.error('❌ Finalize OAuth Error:', data.error);
                throw new Error(data.error || 'Falha ao finalizar autenticação');
              }

              message.success({ 
                content: '✅ Conta conectada com sucesso!',
                key: 'finalizeOAuth',
                duration: 3,
              });

              window.removeEventListener('message', handleOAuthMessage);
              
              // Recarregar página
              setTimeout(() => {
                window.location.reload();
              }, 1500);
              
            } catch (error) {
              console.error('Error finalizing OAuth:', error);
              message.error({ 
                content: 'Erro ao finalizar autenticação',
                key: 'finalizeOAuth',
              });
              window.removeEventListener('message', handleOAuthMessage);
            }
          }
        };

        window.addEventListener('message', handleOAuthMessage);

        // Construir URL OAuth direto no frontend
        // CRITICAL: Must match exactly what backend expects
        const redirectUri = 'https://flowhub-inky.vercel.app/oauth-callback.html';
        const scopes = currentCredential.type === 'googleCalendarOAuth2Api' 
          ? 'https://www.googleapis.com/auth/calendar'
          : 'https://www.googleapis.com/auth/calendar';

        const oauthParams = new URLSearchParams({
          client_id: currentValues.clientId,
          redirect_uri: redirectUri,
          response_type: 'code',
          scope: scopes,
          access_type: 'offline',
          prompt: 'consent',
        });

        const oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?${oauthParams.toString()}`;

        console.log('🔗 OAuth URL constructed:', oauthUrl);
        console.log('📍 Redirect URI:', redirectUri);

        // Abrir popup
        console.log('📱 Showing Modal.info...');
        Modal.info({
          title: '🔐 Autorizar Acesso',
          width: 600,
          content: (
            <div>
              <Paragraph>
                ✅ Credencial criada com sucesso!
              </Paragraph>
              <Paragraph strong style={{ fontSize: 16, color: '#1890ff' }}>
                Agora autorize o acesso à sua conta Google:
              </Paragraph>
              <Button 
                type="primary" 
                icon={<GoogleOutlined />}
                onClick={() => {
                  const width = 600;
                  const height = 700;
                  const left = (window.screen.width - width) / 2;
                  const top = (window.screen.height - height) / 2;
                  window.open(
                    oauthUrl, 
                    'google-oauth',
                    `width=${width},height=${height},left=${left},top=${top}`
                  );
                }}
                block
                size="large"
                style={{ marginTop: 16 }}
              >
                Autorizar com Google
              </Button>
            </div>
          ),
          okText: 'Fechar',
          onCancel: () => {
            window.removeEventListener('message', handleOAuthMessage);
          },
        });
      }
    } catch (error: any) {
      console.error('Error creating OAuth credential:', error);
      message.error({ 
        content: error.message || 'Erro ao criar credencial', 
        key: 'createOAuth',
        duration: 5,
      });
    }
  };

  const getCredentialIcon = (type: string) => {
    const icons: Record<string, any> = {
      googleCalendarOAuth2Api: <GoogleOutlined style={{ fontSize: 24, color: '#4285F4' }} />,
      googleDriveOAuth2Api: <GoogleOutlined style={{ fontSize: 24, color: '#4285F4' }} />,
      googleSheetsOAuth2Api: <GoogleOutlined style={{ fontSize: 24, color: '#4285F4' }} />,
      gmailOAuth2: <GoogleOutlined style={{ fontSize: 24, color: '#4285F4' }} />,
      githubOAuth2Api: <GithubOutlined style={{ fontSize: 24 }} />,
      microsoftOAuth2Api: <WindowsOutlined style={{ fontSize: 24, color: '#00A4EF' }} />,
      slackOAuth2Api: <SlackOutlined style={{ fontSize: 24, color: '#4A154B' }} />,
    };
    return icons[type] || <ApiOutlined style={{ fontSize: 24 }} />;
  };

  return (
    <div>
      <Title level={2} style={{ marginBottom: 4, background: 'linear-gradient(135deg, #7c3aed, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
        Meus Produtos
      </Title>
      <Paragraph type="secondary">
        Gerencie seus produtos e configurações.
      </Paragraph>

      {isLoading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
            <Paragraph style={{ marginTop: 16 }}>Carregando produtos...</Paragraph>
          </div>
        </Card>
      ) : clientProducts.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <AppstoreOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
            <Title level={4} type="secondary">
              Você ainda não contratou nenhum produto
            </Title>
            <Button type="primary" href="/client/marketplace">
              Explorar Marketplace
            </Button>
          </div>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {clientProducts.map((cp) => {
            // Pre-calcular estados
            const hasForm = Boolean(cp.form_id || (cp.product as any)?.form_id);
            const hasFormTrigger = Boolean((cp.product as any)?.has_form_trigger);
            const requiresApproval = cp.requires_form_approval;
            const mustEnforceForm = requiresApproval || hasForm || hasFormTrigger;
            const formStatus = cp.form_approval_status;
            const isFormBlocking = mustEnforceForm && formStatus !== 'approved';
            const isActive = cp.workflow_status === 'active';
            const isFreeProduct = (cp.product?.monthly_price ?? 0) <= 0;

            // OAuth state
            const { requiresApi, hasApiConnected } = computeApiRequirement(cp);
            const isOAuthBlocking = requiresApi && !hasApiConnected;
            
            // Debug: log para verificar detecção de API
            if (cp.product?.name?.includes('google calendar')) {
              console.log('🔍 API Detection for Google Calendar:', {
                product: cp.product?.name,
                requiresApi,
                hasApiConnected,
                isOAuthBlocking,
                requires_oauth: (cp.product as any)?.requires_oauth,
                oauth_credentials: (cp as any).oauth_credentials,
                product_oauth_credentials: (cp.product as any)?.oauth_credentials,
                has_oauth_credentials_configured: (cp as any).has_oauth_credentials_configured,
                has_api_credentials_configured: (cp as any).has_api_credentials_configured,
                api_connected: (cp as any).api_connected,
              });
            }
            
            // Mensagem de status principal
            const getMainStatusMessage = () => {
              const subscriptionRaw = (cp as any).subscription;
              const subscription = Array.isArray(subscriptionRaw) ? subscriptionRaw[0] : subscriptionRaw;
              const paymentStatus = subscription?.payment_status || (cp as any).payment_status;
              const trialEnd = subscription?.trial_end;
              const isInTrial = trialEnd && new Date(trialEnd) > new Date();
              const isPaymentBlocking = !isFreeProduct && !isInTrial && paymentStatus && !['paid', 'succeeded'].includes(paymentStatus);
              
              if (isActive) return null;
              
              // PRIORIDADE 1: Pagamento vencido (VERMELHO)
              if (paymentStatus === 'overdue') {
                return { type: 'error', icon: '⚠️', message: 'Pagamento vencido — regularize para continuar.' };
              }

              // PRIORIDADE 2: Pagamento pendente (VERMELHO)
              if (isPaymentBlocking) {
                return { type: 'error', icon: '💳', message: 'Pagamento pendente. Aguarde confirmação para ativar.' };
              }
              
              // PRIORIDADE 3: Formulário pendente
              if (mustEnforceForm && (!formStatus || formStatus === 'pending_submission')) {
                return { type: 'warning', icon: '⚙️', message: 'Configure o formulário para ativar.' };
              }
              
              // PRIORIDADE 4: Formulário em análise
              if (mustEnforceForm && formStatus === 'submitted') {
                return { type: 'info', icon: '⏳', message: 'Formulário em análise. Aguarde aprovação para ativar.' };
              }
              
              // PRIORIDADE 5: Formulário reprovado
              if (mustEnforceForm && formStatus === 'rejected') {
                return { type: 'error', icon: '❌', message: 'Formulário reprovado. Corrija para ativar.' };
              }
              
              // PRIORIDADE 6: API não configurada
              if (isOAuthBlocking) {
                return { type: 'warning', icon: '🔌', message: 'Conecte a API para ativar o produto.' };
              }
              
              // Tudo configurado: Pronto para ativação
              // Só mostra se TODOS os requisitos estiverem ok
              if (!isFormBlocking && !isOAuthBlocking && !isPaymentBlocking) {
                return { type: 'success', icon: '✅', message: 'Tudo pronto! Ative o produto para começar.' };
              }
              
              return null;
            };
            const mainStatus = getMainStatusMessage();

            return (
              <Col xs={24} md={12} xl={8} key={cp.id}>
                <Card
                  size="small"
                  style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                  bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 16 }}
                >
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 8,
                          background: isActive ? '#e6f7ff' : '#f5f5f5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <SettingOutlined style={{ color: isActive ? '#1677ff' : '#8c8c8c', fontSize: 16 }} />
                      </div>
                      <div>
                        <Text strong style={{ display: 'block', fontSize: 14 }}>
                          {cp.product?.name || 'Produto'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {isFreeProduct ? 'Gratuito' : formatCurrency(cp.product?.monthly_price) + '/mês'}
                        </Text>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Tag color={isActive ? 'green' : 'default'} style={{ margin: 0 }}>
                        {isActive ? 'Ativo' : 'Inativo'}
                      </Tag>
                      <Switch
                        size="small"
                        checked={isActive}
                        loading={togglingProducts.has(cp.id)}
                        disabled={(() => {
                          const subscriptionRaw = (cp as any).subscription;
                          const subscription = Array.isArray(subscriptionRaw) ? subscriptionRaw[0] : subscriptionRaw;
                          const paymentStatus = subscription?.payment_status || (cp as any).payment_status;
                          const trialEnd = subscription?.trial_end;
                          const isInTrial = trialEnd && new Date(trialEnd) > new Date();
                          const isPaymentBlocking = !isFreeProduct && !isInTrial && paymentStatus && !['paid', 'succeeded'].includes(paymentStatus);
                          return togglingProducts.has(cp.id) || (!isActive && (isFormBlocking || isOAuthBlocking || isPaymentBlocking));
                        })()}
                        onChange={(checked) => handleToggleActive(cp.id, checked, cp)}
                      />
                    </div>
                  </div>

                  {/* Main Status Alert */}
                  {mainStatus && (
                    <Alert
                      message={<span>{mainStatus.icon} {mainStatus.message}</span>}
                      type={mainStatus.type as any}
                      style={{ marginBottom: 12, fontSize: 12 }}
                      showIcon={false}
                    />
                  )}

                  {/* Footer */}
                  <div style={{ marginTop: 'auto', paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Contratado em {new Date(cp.created_at).toLocaleDateString('pt-BR')}
                      </Text>
                      <Space size="small">
                        <Button
                          type="link"
                          size="small"
                          icon={<AppstoreOutlined />}
                          onClick={() => handleViewDetails(cp)}
                          style={{ padding: '0 4px' }}
                        >
                          Detalhes
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          icon={<QuestionCircleOutlined />}
                          onClick={() => navigate(`/client/support?workflow=${cp.id}`)}
                          style={{ padding: '0 4px', color: '#52c41a' }}
                        >
                          Suporte
                        </Button>
                      </Space>
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <Drawer
        title={
          <Space>
            <FileSearchOutlined />
            <span>Logs do workflow</span>
          </Space>
        }
        open={logsDrawerOpen}
        width={520}
        onClose={() => {
          setLogsDrawerOpen(false);
          setLogsProduct(null);
        }}
      >
        {logsProduct && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>{logsProduct.product?.name}</Text>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Histórico das últimas execuções e ações realizadas neste workflow.
            </Paragraph>
          </div>
        )}

        {loadingLogs ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin />
          </div>
        ) : productLogs.length ? (
          <Timeline
            items={productLogs.map((log: any) => ({
              color: timelineColor(log.action_type),
              children: (
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  <Text strong>{mapActionLabel(log.action_type)}</Text>
                  {log.description && (
                    <Paragraph style={{ marginBottom: 4 }}>{log.description}</Paragraph>
                  )}
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    {dayjs(log.created_at).format('DD/MM/YYYY HH:mm')}
                  </Paragraph>
                </Space>
              ),
            }))}
          />
        ) : (
          <Empty description="Nenhum registro encontrado" />
        )}
      </Drawer>

      {/* OAuth Connection Modal */}
      <Modal
        title={
          <Space>
            <ApiOutlined />
            <span>Conectar API</span>
          </Space>
        }
        open={oauthModalOpen}
        onOk={handleCreateOAuthCredential}
        onCancel={() => {
          setOauthModalOpen(false);
          setSelectedCredential(null);
          oauthForm.resetFields();
        }}
        okText="Criar Credencial"
        cancelText="Cancelar"
        width={600}
        confirmLoading={createOAuthCredential.isPending}
        destroyOnHidden
      >
        {selectedCredential && (
          <div>
            <div style={{ 
              textAlign: 'center',
              marginBottom: 24,
              padding: 20,
              background: '#fafafa',
              borderRadius: 8,
            }}>
              {getCredentialIcon(selectedCredential.type)}
              <Title level={4} style={{ marginTop: 12, marginBottom: 4 }}>
                {selectedCredential.displayName}
              </Title>
              <Paragraph type="secondary" style={{ margin: 0 }}>
                Preencha as informações da sua aplicação OAuth
              </Paragraph>
            </div>

            <Alert
              message="Como obter as credenciais?"
              description={
                <div>
                  <Paragraph style={{ marginBottom: 8 }}>
                    Para conectar sua conta {selectedCredential.displayName}, você precisa:
                  </Paragraph>
                  <ol style={{ paddingLeft: 20, margin: 0 }}>
                    <li>Criar uma aplicação OAuth no painel de desenvolvedor</li>
                    <li>Copiar o Client ID e Client Secret</li>
                    <li>Preencher os campos abaixo</li>
                    <li>Autorizar o acesso na próxima etapa</li>
                  </ol>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Form form={oauthForm} layout="vertical">
              {selectedCredential.fields?.map((field: any) => (
                <Form.Item
                  key={field.name}
                  label={field.displayName}
                  name={field.name}
                  rules={[
                    {
                      required: field.required,
                      message: `${field.displayName} é obrigatório`,
                    },
                  ]}
                >
                  {field.type === 'password' || field.name.includes('secret') ? (
                    <Input.Password 
                      placeholder={`Digite o ${field.displayName}`}
                      autoComplete="off"
                    />
                  ) : (
                    <Input placeholder={`Digite o ${field.displayName}`} />
                  )}
                </Form.Item>
              ))}
            </Form>

            <Alert
              message="Próximos passos"
              description="Após criar a credencial, você será redirecionado para autorizar o acesso à sua conta."
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          </div>
        )}
      </Modal>

      {/* Form Modal with FormRenderer */}
      <Modal
        title={
          <Space>
            <FormOutlined />
            <span>{formData?.name || 'Preencher Formulário'}</span>
          </Space>
        }
        open={formModalOpen}
        onCancel={() => {
          setFormModalOpen(false);
          setSelectedClientProductId(null);
          setFormData(null);
          setFormSubmitted(false);
          setFormInitialValues(null);
          setFormSubmissionId(null);
          setFormWasApproved(false);
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        {formSubmitted && !formInitialValues ? (
          <Alert
            message="Formulário enviado com sucesso!"
            description="Sua solicitação foi recebida e será processada em breve."
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        ) : loadingForm ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Carregando formulário...</p>
          </div>
        ) : formData && selectedClientProductId ? (
          <>
            {formWasApproved && (
              <Alert
                message="Atenção: formulário aprovado"
                description="Alterar os dados do formulário fará com que ele seja enviado novamente e passe por nova aprovação pelos administradores."
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
              />
            )}
            <FormRenderer
            formId={formData.id}
            formName={formData.name}
            fields={formData.fields}
            settings={formData.settings}
            clientProductId={selectedClientProductId}
            submissionId={formSubmissionId || undefined}
            isPreviouslyApproved={formWasApproved}
            onDraftCreated={(id) => {
              setFormSubmissionId(id);
              // Refresh the products list to reflect draft state (pending_submission)
              if (clientId) queryClient.invalidateQueries({ queryKey: ['client-products', clientId] });
            }}
            onSuccess={handleFormSubmitSuccess}
            initialValues={formInitialValues || undefined}
            readOnly={Boolean(formInitialValues && (clientProducts.find(cp => cp.id === selectedClientProductId)?.form_approval_status === 'submitted'))}
          />
          </>
        ) : (
          <Alert
            message="Erro"
            description="Não foi possível carregar o formulário"
            type="error"
          />
        )}
      </Modal>

      {/* Modal de Detalhes do Produto */}
      <Modal
        title={detailsProduct?.product?.name || 'Produto'}
        open={detailsModalOpen}
        onCancel={() => {
          setDetailsModalOpen(false);
          setDetailsProduct(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailsModalOpen(false)}>
            Fechar
          </Button>,
          <Button
            key="uncontract"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              if (detailsProduct) {
                setDetailsModalOpen(false);
                handleUncontract(detailsProduct.id, detailsProduct.product?.name || 'Produto');
              }
            }}
          >
            Descontratar
          </Button>,
        ]}
        width={700}
      >
        {detailsProduct && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {/* Descrição do produto */}
            {detailsProduct.product?.description && (
              <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 8 }}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {detailsProduct.product.description}
                </Text>
              </div>
            )}

            {/* Alertas importantes */}
            {(detailsProduct as any)?.last_submission?.metadata?.missing_fields && 
             Array.isArray((detailsProduct as any).last_submission.metadata.missing_fields) && 
             ((detailsProduct as any).last_submission.metadata.missing_fields.length > 0) && (
              <Alert
                type="warning"
                message="Campos faltando na última submissão"
                description={`Os seguintes campos não foram preenchidos: ${(detailsProduct as any).last_submission.metadata.missing_fields.join(', ')}`}
                showIcon
              />
            )}

            {detailsProduct.form_approval_status === 'rejected' && detailsProduct.rejection_reason && (
              <Alert
                type="error"
                message="Motivo da Reprovação"
                description={detailsProduct.rejection_reason}
                showIcon
              />
            )}

            <Divider style={{ margin: '12px 0' }} />

            {/* Informações do Contrato */}
            <div>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 12 }}>
                📋 Informações do Contrato
              </Text>
              <div style={{ background: '#fafafa', padding: 14, borderRadius: 8 }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Data de contratação</Text>
                    <div><Text strong>{dayjs(detailsProduct.created_at).format('DD/MM/YYYY [às] HH:mm')}</Text></div>
                  </div>
                  
                  {detailsProduct.next_billing_date && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Próximo pagamento</Text>
                      <div><Text strong>{dayjs(detailsProduct.next_billing_date).format('DD/MM/YYYY')}</Text></div>
                    </div>
                  )}

                  <Divider style={{ margin: '8px 0' }} />
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>Status atual</Text>
                      <div>
                        <Tag color={detailsProduct.is_active ? 'success' : 'default'} style={{ margin: 0 }}>
                          {detailsProduct.is_active ? 'Ativo' : 'Inativo'}
                        </Tag>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Valor mensal</Text>
                      <div>
                        <Text style={{ fontSize: 13 }}>
                          {((detailsProduct.product?.monthly_price ?? 0) <= 0) 
                            ? <Tag color="success">Gratuito</Tag>
                            : <Text strong>{formatCurrency(detailsProduct.product?.monthly_price)}</Text>
                          }
                        </Text>
                      </div>
                    </div>
                  </div>
                </Space>
              </div>
            </div>

            <Divider style={{ margin: '12px 0' }} />

            {/* Formulário e APIs */}
            {(() => {
              const hasForm = Boolean(detailsProduct.form_id || (detailsProduct.product as any)?.form_id);
              const hasFormTrigger = Boolean((detailsProduct.product as any)?.has_form_trigger);
              const mustEnforceForm = detailsProduct.requires_form_approval || hasForm || hasFormTrigger;
              const formStatus = detailsProduct.form_approval_status;

              const allOauthConfigured = Array.isArray(detailsCredentialsData?.credentials) && detailsCredentialsData.credentials.length > 0
                ? detailsCredentialsData.credentials.every((c: any) => c.isConfigured)
                : false;
              const telegramConnected = Boolean(detailsTelegramInfo?.hasCredential);

              const { requiresApi, hasApiConnected: hasApiConnectedDb } = computeApiRequirement(detailsProduct, {
                oauthCreds: detailsCredentialsData?.credentials,
                telegramConnected,
              });

              // Verificar se realmente tem credenciais OAuth necessárias (não apenas workflow)
              // const requiresOAuth = Boolean((detailsProduct.product as any)?.requires_oauth);
              // const hasOAuthCredentials = hasCredentialsRequirement((detailsProduct as any).oauth_credentials) ||
              //   hasCredentialsRequirement((detailsProduct.product as any)?.oauth_credentials) ||
              //   Boolean(detailsCredentialsData?.credentials?.length);

              const isCredentialCheckLoaded = !loadingDetailsCredentials && detailsCredentialsData !== undefined;
              const hasApiConnectedLive = allOauthConfigured || telegramConnected;
              const showApiAlert = requiresApi && isCredentialCheckLoaded && !(hasApiConnectedLive || hasApiConnectedDb);
              
              if (!mustEnforceForm && !requiresApi) return null;
              
              return (
                <div>
                  {showApiAlert && (
                    <Alert
                      type="warning"
                      message="Conexão de API Necessária"
                      description="Este produto requer conexão com uma API externa. Configure as credenciais OAuth para ativar o produto."
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  <div>
                  <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 12 }}>
                    ⚙️ Configurações Necessárias
                  </Text>
                  <div style={{ background: '#fafafa', padding: 14, borderRadius: 8 }}>
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                      {/* Formulário */}
                      {mustEnforceForm && (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <Space size="small">
                              <FormOutlined style={{ color: '#7c3aed' }} />
                              <Text strong style={{ fontSize: 13 }}>Formulário de Configuração</Text>
                            </Space>
                            <Tag color={
                              formStatus === 'approved' ? 'success' :
                              formStatus === 'submitted' ? 'blue' :
                              formStatus === 'rejected' ? 'error' : 'orange'
                            } style={{ margin: 0 }}>
                              {formStatus === 'approved' ? 'Aprovado' :
                               formStatus === 'submitted' ? 'Em análise' :
                               formStatus === 'rejected' ? 'Reprovado' : 'Obrigatório'}
                            </Tag>
                          </div>
                          <Button
                            block
                            type={formStatus === 'approved' ? 'default' : 'primary'}
                            icon={<EditOutlined />}
                            onClick={() => {
                              const formId = detailsProduct.form_id || (detailsProduct.product as any)?.form_id;
                              if (formId) {
                                setDetailsModalOpen(false);
                                handleOpenFormModal(detailsProduct.id, formId, formStatus ?? undefined);
                              }
                            }}
                          >
                            {formStatus === 'approved' ? 'Editar Formulário' : 'Preencher Formulário'}
                          </Button>
                        </div>
                      )}
                      
                      {/* OAuth Credentials - sempre mostrar se houver workflow */}
                      <div>
                        <OAuthCredentialsSection 
                          clientProduct={detailsProduct}
                          onConnect={(credential) => {
                            setDetailsModalOpen(false);
                            handleOpenOAuthModal(credential);
                          }}
                        />
                      </div>

                      {/* Telegram Credentials */}
                      <div>
                        <TelegramCredentialsSection clientProduct={detailsProduct} />
                      </div>
                    </Space>
                  </div>
                </div>
                </div>
              );
            })()}

          </Space>
        )}
      </Modal>
    </div>
  );
};

export default MyProductsPage;
